<template>
  <div class="tk-dialog__field">
    <label class="tk-dialog__label">
      {{ label }}<span v-if="required" class="tk-dialog__label-required" aria-hidden="true">*</span>
      <span v-if="fieldHint" class="tk-dialog__label-hint">{{ fieldHint }}</span>
    </label>
    <v-btn-toggle
      :model-value="mode"
      mandatory
      density="comfortable"
      class="tk-dialog__toggle"
      :disabled="disabled"
      @update:model-value="setMode"
    >
      <v-btn value="FIXED" class="text-none">Same for every lead</v-btn>
      <v-btn value="LEAD_FIELD" class="text-none">A lead field</v-btn>
    </v-btn-toggle>

    <template v-if="mode === 'FIXED'">
      <v-select
        v-if="valueType === 'select'"
        :model-value="value"
        :items="valueItems"
        item-title="title"
        item-value="value"
        variant="outlined"
        density="default"
        hide-details="auto"
        class="tk-dialog__input"
        :rules="[requiredRule]"
        :disabled="disabled"
        @update:model-value="$emit('update:value', $event)"
      />
      <v-text-field
        v-else-if="valueType === 'number'"
        :model-value="value"
        type="number"
        :min="valueMin"
        :placeholder="valuePlaceholder"
        variant="outlined"
        density="default"
        hide-details="auto"
        class="tk-dialog__input"
        :rules="[requiredRule, positiveIntegerRule]"
        :disabled="disabled"
        @update:model-value="$emit('update:value', $event === '' ? null : Number($event))"
      />
      <WorkingHoursTimeField
        v-else-if="valueType === 'time'"
        :model-value="value"
        :rules="[requiredTimeRule]"
        :disabled="disabled"
        @update:model-value="$emit('update:value', $event)"
      />
      <v-text-field
        v-else-if="valueType === 'date'"
        :model-value="value"
        type="date"
        variant="outlined"
        density="default"
        hide-details="auto"
        class="tk-dialog__input"
        :rules="[requiredRule]"
        :disabled="disabled"
        @update:model-value="$emit('update:value', $event)"
      />
    </template>

    <template v-if="mode === 'LEAD_FIELD'">
      <v-select
        :model-value="overrideField"
        :items="overrideOptions"
        item-title="label"
        item-value="key"
        :placeholder="overridePlaceholder"
        variant="outlined"
        density="default"
        hide-details="auto"
        class="tk-dialog__input"
        :rules="[requiredRule]"
        :disabled="disabled || !canUseFilters"
        @update:model-value="$emit('update:overrideField', $event)"
      />
      <p v-if="!canUseFilters" class="tk-dialog__hint tk-dialog__hint--notice">
        Select at least one product above to choose a lead field.
      </p>
      <template v-else>
        <v-select
          v-if="valueType === 'select'"
          :model-value="value"
          :items="valueItems"
          item-title="title"
          item-value="value"
          placeholder="Fallback when the field above is empty"
          variant="outlined"
          density="default"
          hide-details="auto"
          class="tk-dialog__input"
          :rules="[requiredRule]"
          :disabled="disabled"
          @update:model-value="$emit('update:value', $event)"
        />
        <v-text-field
          v-else-if="valueType === 'number'"
          :model-value="value"
          type="number"
          :min="valueMin"
          placeholder="Fallback when the field above is empty"
          variant="outlined"
          density="default"
          hide-details="auto"
          class="tk-dialog__input"
          :rules="[requiredRule, positiveIntegerRule]"
          :disabled="disabled"
          @update:model-value="$emit('update:value', $event === '' ? null : Number($event))"
        />
        <WorkingHoursTimeField
          v-else-if="valueType === 'time'"
          :model-value="value"
          :rules="[requiredTimeRule]"
          :disabled="disabled"
          @update:model-value="$emit('update:value', $event)"
        />
        <p class="tk-dialog__hint">
          Required — used as the fallback whenever that lead field is empty on a given lead.
        </p>
      </template>
    </template>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import WorkingHoursTimeField from '@/components/Campaigns/WorkingHoursTimeField.vue'

defineOptions({ name: 'FieldWithLeadOverride' })

const props = defineProps({
  label: { type: String, required: true },
  fieldHint: { type: String, default: '' },
  value: { type: [String, Number], default: null },
  overrideField: { type: String, default: '' },
  valueType: { type: String, required: true }, // 'number' | 'select' | 'time' | 'date'
  valueItems: { type: Array, default: () => [] },
  valueMin: { type: Number, default: undefined },
  valuePlaceholder: { type: String, default: '' },
  overrideOptions: { type: Array, default: () => [] },
  overridePlaceholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  canUseFilters: { type: Boolean, default: true },
  // ONE_TIME's "Call time" is the one caller that needs this optional --
  // leaving it blank there means the date field above already holds the
  // full moment (date AND time together), not just a calendar date. Every
  // other caller needs the default (required) unchanged.
  required: { type: Boolean, default: true },
})

const emit = defineEmits(['update:value', 'update:overrideField'])

// 'FIXED' = one value for every lead in the campaign; 'LEAD_FIELD' = each
// lead's own value in overrideField sets it, falling back to `value` when
// that field is empty on a given lead. Initialized from whether an
// overrideField is already set (edit mode hydrating an existing campaign),
// and re-synced whenever the parent replaces overrideField wholesale (e.g.
// switching which campaign is being edited) without fighting the user's own
// toggle clicks in between.
const mode = ref(props.overrideField ? 'LEAD_FIELD' : 'FIXED')
watch(
  () => props.overrideField,
  (next) => {
    mode.value = next ? 'LEAD_FIELD' : 'FIXED'
  },
)

function setMode(next) {
  mode.value = next
  if (next === 'FIXED') emit('update:overrideField', '')
}

function requiredRule(v) {
  const empty = v === null || v === undefined || String(v).trim() === ''
  if (!props.required && empty) return true
  return !empty || `${props.label} is required`
}
function requiredTimeRule(v) {
  const raw = String(v ?? '').trim()
  if (!props.required && !raw) return true
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(raw) || `${props.label} is required`
}
function positiveIntegerRule(v) {
  const n = Number(v)
  return (Number.isInteger(n) && n > 0) || 'Must be a positive whole number'
}
</script>

<style scoped>
/* KAN-14: mirrors CreateCampaignDialog's own .tk-dialog__label-required --
   this component renders its own <label> in its own template, so a parent's
   scoped rule of the same name never reaches it and needs its own copy. */
.tk-dialog__label-required {
  color: #b91c1c;
  margin-left: 2px;
  font-weight: 700;
}

.v-theme--dark .tk-dialog__label-required {
  color: #f87171;
}
</style>
